/**
 * "No round template configured" empty state, upgraded the same way the case
 * panel was: before asking the recruiter to type the final interviewer and bar
 * raiser, READ them from the role's kick-off sheet.
 *
 * The old copy said "(from the kick-off sheet)" on the fields while nothing was
 * ever pre-filled from it — an instruction dressed up as a feature. Now the
 * sheet's final-round section is parsed (proxy POST /panel-suggest with
 * `stage: 'final'`), the two seats are offered, and the recruiter confirms them
 * in the template modal. Nothing is saved until they do, and once a template
 * exists this state is gone — so the suggestion happens once, not on every visit.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import type { FinalRoundSuggestion } from '../../services/taActionBoardApi';
import { fullNameFromEmail } from './format';
import { useKickoffSuggestion } from './useKickoffSuggestion';

export function NoRoundTemplateState({
  postingId,
  myEmail,
  onConfigure,
}: {
  postingId: string;
  myEmail: string;
  /** Opens the round-template modal, optionally pre-filled from the sheet. */
  onConfigure: (suggestion: FinalRoundSuggestion | null) => void;
}) {
  const { state, connectAndFetch, setLoading, connecting } = useKickoffSuggestion<FinalRoundSuggestion>(
    postingId,
    'final',
    myEmail
  );

  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center">
      <p className="text-sm font-semibold text-gray-700">No round template configured</p>

      {state.phase === 'loading' ? (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner className="h-3 w-3" /> Reading the kick-off sheet for the final round…
        </p>
      ) : state.phase === 'found' ? (
        <>
          <p className="text-xs text-gray-600">
            From the{' '}
            {state.kickoffUrl ? (
              <a href={state.kickoffUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                kick-off sheet ↗
              </a>
            ) : (
              'kick-off sheet'
            )}
            :{' '}
            {/* BOTH seats are always named, including the one the sheet did not
                fill: "no bar raiser found" tells the recruiter which field they
                still have to type. Omitting it would read as "all set". */}
            <span className="font-semibold text-gray-800">
              {seatLabel('final', state.suggestion.finalInterviewer)} ·{' '}
              {seatLabel('bar raiser', state.suggestion.barRaiser)}
            </span>
            {state.suggestion.interviewMinutes ? ` · ${state.suggestion.interviewMinutes} min` : ''}
          </p>
          {/* Named in the section but unplaceable: the sheet cannot say which
              seat a third person takes, so the recruiter decides. */}
          {(state.suggestion.alsoFound?.length ?? 0) > 0 && (
            <p className="text-xs text-gray-400">
              Also named: {state.suggestion.alsoFound!.map(fullNameFromEmail).join(', ')}
            </p>
          )}
          {state.unresolved.length > 0 && (
            <p className="text-xs text-gray-400">
              Could not match: {state.unresolved.join(', ')} — add them by hand if they belong here.
            </p>
          )}
          <Button size="sm" className="mt-1" onClick={() => onConfigure(state.suggestion)}>
            Review &amp; save template
          </Button>
        </>
      ) : state.phase === 'connect' ? (
        <>
          <p className="text-xs text-gray-500">
            Connect your Google account and the board pre-fills the final interviewer and bar raiser
            from the role's kick-off sheet. Configuring by hand works too.
          </p>
          <div className="mt-1 flex flex-wrap justify-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setLoading();
                void connectAndFetch();
              }}
              loading={connecting}
            >
              Suggest from kick-off sheet
            </Button>
            <Button size="sm" onClick={() => onConfigure(null)}>
              Configure round template
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            {state.reason}
            {state.kickoffUrl && (
              <>
                {' '}
                <a href={state.kickoffUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                  Open the kick-off sheet ↗
                </a>
              </>
            )}
          </p>
          <Button size="sm" className="mt-1" onClick={() => onConfigure(null)}>
            Configure round template
          </Button>
        </>
      )}
    </div>
  );
}

/** "Nora Vogel (final)", or "no final found" when the sheet did not fill it. */
function seatLabel(seat: string, email: string): string {
  return email ? `${fullNameFromEmail(email)} (${seat})` : `no ${seat} found`;
}
